import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { STRIPE_PRICES, PricingPlan, EARLY_BIRD_LIMIT } from "@/config/stripe";
import { toast } from "sonner";
import { isIOSNative, getPaymentProvider } from "@/utils/platform";
import { usePlatformPurchase } from "./usePlatformPurchase";
import { trackSubscriptionPurchase } from "@/utils/metaTracking";
interface SubscriptionStatus {
  subscribed: boolean;
  priceId: string | null;
  subscriptionEnd: string | null;
  plan: PricingPlan | null;
  isEarlyBird: boolean;
  earlyBirdSpotsLeft: number;
  isTestAccount: boolean;
  isIOSPurchase: boolean;
}

export const useSubscription = () => {
  const { user, session } = useAuth();
  const paymentProvider = getPaymentProvider();
  const isIOS = isIOSNative();

  const [subscription, setSubscription] = useState<SubscriptionStatus>({
    subscribed: false,
    priceId: null,
    subscriptionEnd: null,
    plan: null,
    isEarlyBird: false,
    earlyBirdSpotsLeft: EARLY_BIRD_LIMIT,
    isTestAccount: false,
    isIOSPurchase: false,
  });
  const [loading, setLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    // Skip if no session - don't block app loading
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);

    // Add timeout to prevent hanging on slow/failed requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      clearTimeout(timeoutId);

      // Handle error gracefully - don't block app
      if (error) {
        console.error("[Subscription] Check failed:", error);
        setLoading(false);
        return;
      }

      // Handle no_session response (auth not ready yet)
      if (data?.no_session) {
        console.log("[Subscription] No session yet, will retry");
        setLoading(false);
        return;
      }

      // Determine plan from price ID
      let plan: PricingPlan | null = null;
      if (data.price_id) {
        if (
          data.price_id === STRIPE_PRICES.yearly.earlyBird.price_id ||
          data.price_id === STRIPE_PRICES.yearly.regular.price_id
        ) {
          plan = "yearly";
        } else if (
          data.price_id === STRIPE_PRICES.monthly.earlyBird.price_id ||
          data.price_id === STRIPE_PRICES.monthly.regular.price_id
        ) {
          plan = "monthly";
        }
      }

      setSubscription({
        subscribed: data.subscribed,
        priceId: data.price_id,
        subscriptionEnd: data.subscription_end,
        plan,
        isEarlyBird: data.is_early_bird || false,
        earlyBirdSpotsLeft: data.earlyBirdSpotsLeft ?? EARLY_BIRD_LIMIT,
        isTestAccount: data.is_test_account || false,
      // Consider iOS purchase if explicitly flagged OR if it's a manual grant 
      // (since admin grants for iOS users should route to App Store, not Stripe)
      isIOSPurchase: data.is_ios_purchase || data.is_manual_grant || false,
      });
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("[Subscription] Error checking subscription:", error);
      // Don't block app on subscription check failure
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  const createCheckout = async (plan: PricingPlan) => {
    // On iOS, we should not use Stripe - this is handled by usePlatformPurchase
    if (isIOS) {
      toast.info("Please use the purchase button for iOS subscriptions");
      return;
    }

    if (!session?.access_token) {
      toast.error("Please sign in to subscribe");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { plan },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      if (data.url) {
        // Redirect in same window to preserve session
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error creating checkout:", error);
      toast.error("Failed to start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCustomerPortal = async () => {
   // If subscription was purchased via iOS (even if viewing on web), direct to App Store
   if (isIOS || subscription.isIOSPurchase) {
      toast.info("Opening App Store subscriptions");
     // On native iOS, use itms-apps scheme; on web, use HTTPS link
     const appStoreUrl = isIOS 
       ? "itms-apps://apps.apple.com/account/subscriptions"
       : "https://apps.apple.com/account/subscriptions";
     window.open(appStoreUrl, "_blank");
      return;
    }
    
    // For manual grants (admin-set premium), don't try Stripe portal
    if (subscription.priceId === 'manual_grant') {
      toast.info("Your premium access was granted by admin. No subscription to manage.");
      return;
    }

    if (!session?.access_token) {
      toast.error("Please sign in to manage subscription");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Handle case where user has no Stripe customer
      if (data.code === "NO_CUSTOMER") {
        toast.info("No subscription found. Please subscribe first.");
        return;
      }

      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
      toast.error("Failed to open subscription management. Please try again.");
    }
  };

  // Check subscription on mount and when user changes
  useEffect(() => {
    if (user && session) {
      checkSubscription();
    }
  }, [user, session, checkSubscription]);

  // Refresh subscription when app comes to foreground (critical for iOS)
  useEffect(() => {
    if (!user || !session) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("[Subscription] App foregrounded - refreshing status");
        checkSubscription();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [user, session, checkSubscription]);

  // Check for checkout success/cancel in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get("checkout");

    if (checkoutStatus === "success") {
      toast.success("Welcome to Premium! Your subscription is now active.");
      // Clean up URL first
      window.history.replaceState({}, "", window.location.pathname);

      // Track Meta purchase event for Stripe web checkout
      trackSubscriptionPurchase("web_premium", 9.99);

      // Multiple checks with increasing delays to catch Stripe processing
      const delays = [500, 2000, 5000, 10000, 15000, 20000];
      delays.forEach((delay) => {
        setTimeout(() => {
          checkSubscription();
        }, delay);
      });
    } else if (checkoutStatus === "canceled") {
      toast.info("Checkout was canceled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [checkSubscription]);

  // Periodically refresh subscription status (every 30 seconds when tab is active)
  useEffect(() => {
    if (!user || !session) return;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        checkSubscription();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [user, session, checkSubscription]);

  return {
    subscription,
    loading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    paymentProvider,
    isIOS,
  };
};
